import { useRef, useEffect, useCallback, useMemo } from 'react'

interface UseRetryOptions {
  maxRetries: number
  onExhausted: (errorMsg: string, retryFn: () => void) => void
}

interface UseRetryReturn {
  /** Schedule a retry with exponential backoff. Returns true if retry was scheduled, false if exhausted. */
  scheduleRetry: (retryFn: () => void, errorMsg: string) => boolean
  /** Reset the retry counter (call on successful completion) */
  reset: () => void
  /** Cancel any pending retry timer */
  cancel: () => void
}

export function useRetry(options: UseRetryOptions): UseRetryReturn {
  const { maxRetries, onExhausted } = options
  const attemptsRef = useRef(0)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => {
      if (timerRef.current !== null) clearTimeout(timerRef.current)
    }
  }, [])

  const cancel = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }, [])

  const reset = useCallback(() => {
    attemptsRef.current = 0
  }, [])

  const scheduleRetry = useCallback(
    (retryFn: () => void, errorMsg: string): boolean => {
      if (attemptsRef.current < maxRetries) {
        attemptsRef.current++
        const delay = attemptsRef.current * 1000
        timerRef.current = setTimeout(retryFn, delay)
        return true
      }
      attemptsRef.current = 0
      onExhausted(errorMsg, retryFn)
      return false
    },
    [maxRetries, onExhausted]
  )

  return useMemo(
    () => ({ scheduleRetry, reset, cancel }),
    [scheduleRetry, reset, cancel]
  )
}
